/**
 * Enhanced Analytics Page
 * Displays key metrics from all RCM Platform services
 * Provides links to detailed analytics in each service
 * Features collapsible sidebar navigation for all service analytics
 */

import React, { useState, useEffect } from 'react';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  Clock,
  Brain,
  Zap,
  Users,
  FileText,
  Activity,
  ArrowRight,
  RefreshCw,
  ExternalLink,
  PieChart,
  Target,
  Shield,
} from 'lucide-react';
import { analyticsService } from '../../services/analyticsService';
import { SERVICE_PORTS } from '../../utils/constants';
import { buildServiceOrigin } from '../../components/ServiceFrame';
import AnalyticsSidebar from '../../components/analytics/AnalyticsSidebar';
import { TEAM_ANALYTICS_EMBED_REGISTRY } from '../../config/rcmServiceCodes';
import { DEFAULT_CROSS_SERVICE_TIME_RANGE } from '@saas-foundation/shared-frontend/analytics';
import { SafeIframe } from '@saas-foundation/shared-frontend/iframeEmbed';

const Analytics = () => {
  const [timeRange, setTimeRange] = useState(DEFAULT_CROSS_SERVICE_TIME_RANGE);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [summary, setSummary] = useState(null);
  const [activeService, setActiveService] = useState(null);
  const [activeTab, setActiveTab] = useState(null);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const allAnalytics = await analyticsService.getAllAnalytics(timeRange);
      setAnalytics(allAnalytics);
      setSummary(analyticsService.buildSummaryFromAllAnalytics(allAnalytics));
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setSummary(analyticsService.buildSummaryFromAllAnalytics(null));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num || 0);
  };

  const getServiceUrl = (serviceName) => {
    const port = SERVICE_PORTS[serviceName]?.FRONTEND || 3000;
    // Correct origin per deployment: <slug>.<domain> on real hosts, host:port on localhost/IP.
    return `${buildServiceOrigin(port)}/analytics`;
  };

  const getColorClasses = (color) => {
    const colors = {
      blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
      red: { bg: 'bg-red-100', text: 'text-red-600' },
      green: { bg: 'bg-green-100', text: 'text-green-600' },
      yellow: { bg: 'bg-yellow-100', text: 'text-yellow-600' },
      purple: { bg: 'bg-purple-100', text: 'text-purple-600' },
    };
    return colors[color] || colors.blue;
  };

  const MetricCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'blue', onClick, link }) => {
    const colorClasses = getColorClasses(color);
    return (
      <div
        className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow ${
          onClick || link ? 'hover:border-blue-500 cursor-pointer' : ''
        }`}
        onClick={onClick || (link ? () => window.open(link, '_blank') : undefined)}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              {Icon && (
                <div className={`p-2 rounded-lg ${colorClasses.bg}`}>
                  <Icon className={`w-5 h-5 ${colorClasses.text}`} />
                </div>
              )}
              <h3 className="text-sm font-medium text-gray-600">{title}</h3>
            </div>
          <div className="mt-2">
            <p className="text-2xl font-bold text-gray-900">{value}</p>
            {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
            {trend && trendValue && (
              <div className={`flex items-center mt-2 text-sm ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                {trend === 'up' ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
                <span>{trendValue}</span>
              </div>
            )}
          </div>
        </div>
        {(onClick || link) && (
          <ArrowRight className="w-5 h-5 text-gray-400" />
        )}
      </div>
    </div>
    );
  };

  const ServiceSection = ({ title, icon: Icon, metrics, insights, link, color = 'blue' }) => {
    const colorClasses = getColorClasses(color);
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${colorClasses.bg}`}>
              <Icon className={`w-6 h-6 ${colorClasses.text}`} />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          </div>
        {link && (
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            <span>View Details</span>
            <ExternalLink className="w-4 h-4" />
          </a>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {metrics.map((metric, index) => (
          <MetricCard key={index} {...metric} color={color} />
        ))}
      </div>

      {insights && insights.length > 0 && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Key Insights</h3>
          <div className="space-y-2">
            {insights.map((insight, index) => (
              <div key={index} className="flex items-start space-x-2 text-sm text-gray-600">
                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>{insight}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-2" />
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  // Always show the page, even if data is null - show zeros/defaults instead

  // Use default values if analytics or summary is null
  const safeSummary = summary || {
    denials: {
      totalDenials: 0,
      denialRate: 0,
      totalRecovered: 0,
      pendingAppeals: 0,
      aiAccuracy: 0,
    },
    claims: {
      totalProcessed: 0,
      successRate: 0,
      totalCharged: 0,
      totalPaid: 0,
      automationRate: 0,
    },
    charges: {
      totalCharges: 0,
      totalAmount: 0,
      avgChargeAmount: 0,
    },
  };

  const safeAnalytics = analytics || {
    denials: null,
    claims: null,
    charges: null,
    insurance: null,
    priorAuth: null,
  };

  // Prepare metrics for each service
  const denialMetrics = [
    {
      title: 'Total Denials',
      value: formatNumber(safeSummary.denials.totalDenials),
      subtitle: `${safeSummary.denials.denialRate.toFixed(1)}% denial rate`,
      icon: AlertTriangle,
      color: 'red',
    },
    {
      title: 'Recovered Amount',
      value: formatCurrency(safeSummary.denials.totalRecovered),
      subtitle: 'Total recovered',
      icon: DollarSign,
      color: 'green',
    },
    {
      title: 'Pending Appeals',
      value: formatNumber(safeSummary.denials.pendingAppeals),
      subtitle: 'Awaiting review',
      icon: Clock,
      color: 'yellow',
    },
    {
      title: 'AI Accuracy',
      value: `${safeSummary.denials.aiAccuracy.toFixed(1)}%`,
      subtitle: 'Prediction accuracy',
      icon: Brain,
      color: 'purple',
    },
  ];

  const claimsMetrics = [
    {
      title: 'Total Processed',
      value: formatNumber(safeSummary.claims.totalProcessed),
      subtitle: 'Claims processed',
      icon: FileText,
      color: 'blue',
    },
    {
      title: 'Success Rate',
      value: `${safeSummary.claims.successRate.toFixed(1)}%`,
      subtitle: 'Processing success',
      icon: CheckCircle,
      color: 'green',
    },
    {
      title: 'Total Charged',
      value: formatCurrency(safeSummary.claims.totalCharged),
      subtitle: 'Amount charged',
      icon: DollarSign,
      color: 'blue',
    },
    {
      title: 'Total Paid',
      value: formatCurrency(safeSummary.claims.totalPaid),
      subtitle: 'Amount received',
      icon: CheckCircle,
      color: 'green',
    },
  ];

  const denialInsights = safeAnalytics.denials?.aiInsights?.key_insights?.map(
    (insight) => insight.insight || insight
  ) || [];

  const claimsInsights = safeAnalytics.claims?.aiInsights?.key_insights || [];

  /** Iframe apps run on different ports than AgenticRCM — they do not share localStorage. Pass org + JWT like ServiceFrame. */
  const appendEmbedAuthToParams = (params) => {
    if (typeof window === 'undefined') return;
    try {
      const orgId =
        localStorage.getItem('selectedOrganizationId') ||
        localStorage.getItem('organizationId') ||
        localStorage.getItem('organization_id') ||
        '';
      if (orgId) params.set('organization_id', orgId);
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token') || '';
      if (token) params.set('auth_token', token);
    } catch {
      /* ignore */
    }
  };

  // Get service URL for iframe embedding with proper tab routing
  const getServiceAnalyticsUrl = (serviceId, tabId) => {
    const servicePortMap = {
      eligibility: SERVICE_PORTS.INSURANCE_VERIFICATION?.FRONTEND || 3150,
      priorAuth: SERVICE_PORTS.PRIOR_AUTHORIZATION?.FRONTEND || 3151,
      charges: SERVICE_PORTS.CHARGE_CAPTURE?.FRONTEND || 3152,
      claims: SERVICE_PORTS.CLAIMS_PROCESSING?.FRONTEND || 3153,
      arPosting: SERVICE_PORTS.AR_POSTING?.FRONTEND || 3155,
      denial: SERVICE_PORTS.DENIAL_MANAGEMENT?.FRONTEND || 3154,
      patientBilling: SERVICE_PORTS.PATIENT_ACCOUNT?.FRONTEND || 3110,
      ediPlatform: SERVICE_PORTS.EDI_PLATFORM?.FRONTEND || 3010,
      // Backward compatibility for older sidebar ids
      clearingHouse: SERVICE_PORTS.EDI_PLATFORM?.FRONTEND || 3010,
      remittanceAdvice: SERVICE_PORTS.REMITTANCE_ADVICE?.FRONTEND || 3157,
      contractManagement: SERVICE_PORTS.CONTRACT_MANAGEMENT?.FRONTEND || 3156,
      compliance: SERVICE_PORTS.RCM_COMPLIANCE?.FRONTEND || 3158,
    };
    const port = servicePortMap[serviceId] || 3000;

    // Insurance Verification can 404 on deep-link path /analytics on some hosts.
    // Use root path there to avoid requiring server-side SPA rewrite rules.
    const serviceBasePathMap = {
      eligibility: '/',
    };
    const basePath = serviceBasePathMap[serviceId] || '/analytics';

    const params = new URLSearchParams();
    // Keep embedded first/explicit for services that key UI mode from URL parsing.
    params.set('embedded', 'true');

    // Denial / Claims / ChargeCapture / PriorAuth / ARPosting have had sidebar mode
    // regressions when tab deep-links mutate query state internally. Parent keeps
    // the embed signal stable and sends tab explicitly.
    if (tabId) {
      params.set('tab', tabId);
    }

    appendEmbedAuthToParams(params);

    // Correct origin per deployment: <slug>.<domain> on real hosts, host:port on localhost/IP.
    return `${buildServiceOrigin(port)}${basePath}?${params.toString()}`;
  };

  // Render content based on active service and tab
  const renderContent = () => {
    // If no specific service/tab selected, show overview dashboard
    if (!activeService) {
      return (
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
              <p className="mt-2 text-sm text-gray-600">
                Cross-service analytics and insights from all RCM Platform services
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
                <option value="1y">Last year</option>
              </select>
              <button
                onClick={fetchAnalytics}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Refresh</span>
              </button>
            </div>
          </div>

          {/* Overall Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <MetricCard
              title="Total Revenue"
              value={formatCurrency(
                (safeSummary.claims.totalPaid || 0) + (safeSummary.denials.totalRecovered || 0)
              )}
              subtitle="Combined revenue"
              icon={DollarSign}
              color="green"
            />
            <MetricCard
              title="Total Claims"
              value={formatNumber(safeSummary.claims.totalProcessed || 0)}
              subtitle="Processed claims"
              icon={FileText}
              color="blue"
            />
            <MetricCard
              title="Denial Rate"
              value={`${safeSummary.denials.denialRate.toFixed(1)}%`}
              subtitle="Overall denial rate"
              icon={AlertTriangle}
              color="red"
            />
            <MetricCard
              title="Automation Rate"
              value={`${safeSummary.claims.totalProcessed > 0 ? ((safeSummary.claims.automationRate / safeSummary.claims.totalProcessed) * 100).toFixed(1) : '0.0'}%`}
              subtitle="AI automation"
              icon={Zap}
              color="purple"
            />
          </div>

          {/* Team analytics — per-service embedded SPAs (Denial ships first; others placeholder) */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-lg bg-violet-100">
                  <Users className="w-6 h-6 text-violet-700" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Team analytics</h2>
                  <p className="text-sm text-gray-600 mt-0.5">
                    Actual vs goals by assignee (including Kai) for each RCM service. Live embeds open the service&apos;s
                    Team Analytics tab; add the same tab in other services to enable more tiles.
                  </p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {TEAM_ANALYTICS_EMBED_REGISTRY.map((svc) => (
                <div
                  key={svc.rcmServiceCode}
                  className="rounded-lg border border-gray-200 overflow-hidden bg-gray-50"
                >
                  <div className="px-4 py-2 border-b border-gray-200 bg-white flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-900">{svc.label}</span>
                    <span className="text-xs text-gray-500 font-mono">{svc.rcmServiceCode}</span>
                  </div>
                  {svc.available ? (
                    <SafeIframe
                      title={`${svc.label} team analytics`}
                      serviceName={svc.label}
                      src={getServiceAnalyticsUrl(svc.sidebarServiceId, svc.tab)}
                      allow="fullscreen"
                      style={{ minHeight: '420px', height: '480px' }}
                    />
                  ) : (
                    <div className="flex items-center justify-center text-sm text-gray-500 px-4 py-16 bg-white text-center">
                      Coming soon — implement Team Analytics in this service&apos;s analytics app, then enable the embed
                      in AgenticRCM <span className="font-mono text-xs">rcmServiceCodes.js</span>.
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Service-Specific Analytics */}
          <div className="space-y-6">
            {/* Denial Management */}
            <ServiceSection
              title="Denial Management"
              icon={AlertTriangle}
              metrics={denialMetrics}
              insights={denialInsights}
              link={getServiceUrl('DENIAL_MANAGEMENT')}
              color="red"
            />

            {/* Claims Processing */}
            <ServiceSection
              title="Claims Processing"
              icon={FileText}
              metrics={claimsMetrics}
              insights={claimsInsights}
              link={getServiceUrl('CLAIMS_PROCESSING')}
              color="blue"
            />

            {/* Charge Management */}
            <ServiceSection
              title="Charge Management"
              icon={DollarSign}
              metrics={[
                {
                  title: 'Total Charges',
                  value: formatNumber(safeSummary.charges.totalCharges),
                  subtitle: 'Charges captured',
                  icon: FileText,
                  color: 'blue',
                },
                {
                  title: 'Total Amount',
                  value: formatCurrency(safeSummary.charges.totalAmount),
                  subtitle: 'Charge amount',
                  icon: DollarSign,
                  color: 'green',
                },
                {
                  title: 'Avg Charge',
                  value: formatCurrency(safeSummary.charges.avgChargeAmount),
                  subtitle: 'Per charge',
                  icon: Target,
                  color: 'purple',
                },
              ]}
              link={getServiceUrl('CHARGE_CAPTURE')}
              color="green"
            />

            {/* Insurance Verification - Placeholder */}
            <ServiceSection
              title="Insurance Verification"
              icon={Shield}
              metrics={[
                {
                  title: 'Verifications',
                  value: 'N/A',
                  subtitle: 'Coming soon',
                  icon: Shield,
                  color: 'blue',
                },
              ]}
              link={getServiceUrl('INSURANCE_VERIFICATION')}
              color="blue"
            />

            {/* Prior Authorization - Placeholder */}
            <ServiceSection
              title="Prior Authorization"
              icon={CheckCircle}
              metrics={[
                {
                  title: 'Authorizations',
                  value: 'N/A',
                  subtitle: 'Coming soon',
                  icon: CheckCircle,
                  color: 'green',
                },
              ]}
              link={getServiceUrl('PRIOR_AUTHORIZATION')}
              color="green"
            />
          </div>
          </div>
        </div>
      );
    }

    // Otherwise, embed the specific service's analytics page
    const serviceUrl = getServiceAnalyticsUrl(activeService, activeTab);
    
    // Log for debugging
    console.log('[Analytics] Loading service analytics:', {
      service: activeService,
      tab: activeTab,
      url: serviceUrl
    });
    
    return (
      <div className="flex-1 overflow-hidden bg-white">
        <SafeIframe
          key={`${activeService}-${activeTab}`}
          src={serviceUrl}
          title={`${activeService} Analytics - ${activeTab || 'Overview'}`}
          serviceName={`${activeService} analytics`}
          allow="fullscreen"
          style={{ minHeight: '100vh' }}
        />
      </div>
    );
  };

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Collapsible Sidebar */}
      <AnalyticsSidebar
        activeService={activeService}
        activeTab={activeTab}
        onServiceChange={setActiveService}
        onTabChange={setActiveTab}
      />

      {/* Main Content */}
      {renderContent()}
    </div>
  );
};

export default Analytics;
